import React, { useState } from 'react';
import axios from 'axios';
import { graphql } from 'gatsby';
import { getAttributeValue, formToJSON } from './utils';
import { WebformDebug, WebformInput, WebformSelect, WebformTextarea, WebformCheckbox, WebformCheckboxGroup, WebformText } from './components';
export const DEFAULT_SUBMIT_LABEL = 'Submit';
export class WebformError extends Error {
    constructor(response) {
        super();
        this.response = response;
    }
}
/**
 * Render single webform element.
 */
export function renderWebformElement(element, error, CustomComponent) {
    const customComponentAPI = {
        error
    };
    // Render using custom compoennt if provided:
    if (CustomComponent) {
        return <CustomComponent element={element} {...customComponentAPI}/>;
    }
    // Othervise select renderer based on element type:
    switch (element.type) {
        case 'textfield':
            return <WebformInput element={{ ...element, type: 'text' }} {...customComponentAPI}/>;
        case 'textarea':
            return <WebformTextarea element={element} {...customComponentAPI}/>;
        case 'tel':
        case 'number':
        case 'email':
        case 'hidden':
            return <WebformInput element={element} {...customComponentAPI}/>;
        case 'checkbox':
        case 'radio':
            /** Render single checkbox or radio element. */
            return <WebformCheckbox element={element} {...customComponentAPI}/>;
        case 'checkboxes':
            return <WebformCheckboxGroup element={{ ...element, type: 'checkbox' }} {...customComponentAPI}/>;
        case 'radios':
            return <WebformCheckboxGroup element={{ ...element, type: 'radio' }} {...customComponentAPI}/>;
        case 'select':
            return <WebformSelect element={element} {...customComponentAPI}/>;
        case 'processed_text':
            return <WebformText element={element} {...customComponentAPI}/>;
        // Submit button
        case 'webform_actions':
            return (<div className="form-group">
					<button type="submit">{getAttributeValue('#submit__label', element) || DEFAULT_SUBMIT_LABEL}</button>
				</div>);
        // Unknown element type -> render as json string
        default:
            return <WebformDebug element={element} error={error}/>;
    }
}
/**
 * Drupal webform react component.
 */
const Webform = ({ webform, customComponents, ...props }) => {
    const [errors, setErrors] = useState({});
    const submitHandler = async (event) => {
        event.preventDefault();
        const target = event.currentTarget;
        // Clear errors from previous submit.
        setErrors({});
        // Remove lingering css classes from previous submits.
        target.classList.remove('form-submitting', 'form-error', 'form-submitted');
        if ((!props.onValidate || props.onValidate(event)) && target.checkValidity()) {
            // Let css know that this form was validated and is being submitted.
            target.classList.add('was-validated', 'form-submitting');
            // Serialize form data.
            const data = formToJSON(target.elements);
            // Post process serialized data:
            // Some webform elements require specialized data formatting.
            for (const element of webform.elements) {
                if (data[element.name]) {
                    switch (element.type) {
                        case 'checkbox':
                            data[element.name] = data[element.name][0];
                            break;
                    }
                }
            }
            try {
                // If onSubmit returns false skip submitting to API.
                if (props.onSubmit && (await props.onSubmit(data, event)) === false) {
                    target.classList.replace('form-submitting', 'form-submitted');
                    return;
                }
                // Submit form to API.
                const response = await axios.post(props.endpoint, {
                    ...props.extraData,
                    ...data,
                    webform_id: webform.drupal_internal__id
                });
                if (response.data.error) {
                    throw new WebformError(response);
                }
                // Convey current form state.
                target.classList.replace('form-submitting', 'form-submitted');
                props.onSuccess && props.onSuccess(response.data, event);
            }
            catch (err) {
                // API should return error structure if validation fails.
                // We use that to render error messages to the form.
                if (err.response && err.response.data.error) {
                    setErrors(err.response.data.error);
                }
                // Convey current form state.
                target.classList.replace('form-submitting', 'form-error');
                props.onError && props.onError(err, event);
            }
        }
        else {
            // Let css know this form was validated.
            target.classList.add('was-validated', 'form-error');
        }
    };
    return (<form onSubmit={submitHandler} id={props.id} className={props.className} style={props.style} noValidate={props.noValidate} data-webform-id={webform.drupal_internal__id}>
			
			{webform.elements.map(element => (<React.Fragment key={element.name}>
					{renderWebformElement(element, errors[element.name], customComponents[element.type])}
				</React.Fragment>))}

			
			{webform.elements.find(element => element.type === 'webform_actions') === undefined && (<button type="submit">{DEFAULT_SUBMIT_LABEL}</button>)}
		</form>);
};
Webform.defaultProps = {
    customComponents: {}
};
export default Webform;
/**
 * @deprecated I'm going to remove dependency to Gatsby in later versions.
 */
export const query = graphql `
	fragment SimpleWebform on webform__webform {
		drupal_internal__id
		elements {
			name
			type
			attributes {
				name
				value
			}
		}
	}
`;
//# sourceMappingURL=Webform.jsx.map